import React, { useState, useMemo, useEffect } from "react";
import { 
  schema, 
  TableSchema,
  QueryBuilderProps,
  QueryBuilderConfig 
} from "../../types/query.types";
import {
  getBmpLayersList,
  bmpBaseTableValue,
  isBmpBaseTable,
  getBmpLayerIdFromBaseTable,
  getBmpLayerUrl,
  getBmpLayerById,
} from "../../lib/bmp-layers";
import { supabase } from "../../lib/supabase";
import { executeComponentQuery } from "../../utils/query-builder";
import { parseSelectSql } from "../../utils/parse-select-sql";
import { removeConcatCommaArtifacts } from "../../utils/concat-field-sanitize";

type Join = {
  table: string;
  on: {
    localField: string;
    foreignField: string;
  };
};

type Filter = {
  field: string;
  operator: "=" | ">" | "<" | ">=" | "<=" | "ILIKE";
  value: string;
};

type AggregationItem = {
  field: string;
  agreggation: string;
  /** Expresión dentro de SUM/AVG/… cuando no es una sola columna */
  innerSql?: string;
};

const OPERATORS = ["=", ">", "<", ">=", "<=", "ILIKE"] as const;
const AGGREGATE_FUNCTIONS = ["", "COUNT", "SUM", "AVG", "MIN", "MAX"];

type AggregateFunctionType = "COUNT" | "SUM" | "AVG" | "MIN" | "MAX";

// Descripciones de las funciones de agregación
const AGGREGATE_DESCRIPTIONS: Record<AggregateFunctionType, string> = {
  "COUNT": "Cuenta el número de filas no nulas",
  "SUM": "Suma de valores numéricos",
  "AVG": "Promedio de valores numéricos",
  "MIN": "Valor mínimo",
  "MAX": "Valor máximo"
};


const DEFAULT_LIMIT = 5000;

const QueryBuilder: React.FC<QueryBuilderProps> = ({ 
  onSaveConfig, 
  onSaveSql, 
  onSaved,
  initialConfig,
  initialSql: _initialSql,
}) => {
  const [baseTable, setBaseTable] = useState<string>(initialConfig?.baseTable || "");
  const [selectedFields, setSelectedFields] = useState<string[]>(initialConfig?.selectedFields || []);
  const [joins, setJoins] = useState<Join[]>(initialConfig?.joins || []);
  const [filters, setFilters] = useState<Filter[]>(initialConfig?.filters || []);
  const [groupByFields, setGroupByFields] = useState<string[]>(initialConfig?.groupByFields || []);
  const [limit, setLimit] = useState<number>(
    initialConfig?.limit != null ? initialConfig.limit : DEFAULT_LIMIT
  );
  const [offset, setOffset] = useState<number>(initialConfig?.offset || 0);
  const [orderByRaw, setOrderByRaw] = useState<string>(initialConfig?.orderByRaw || "");

  // Estado para funciones de agregación (para la UI)
  const [aggregateConfig, setAggregateConfig] = useState<{
    [field: string]: {
      function: string;
      alias: string;
      innerSql?: string;
    }
  }>({});

  const [isConfigSaved, setIsConfigSaved] = useState(false);
  const [isSqlSaved, setIsSqlSaved] = useState(false);
  const [bmpLayerSchema, setBmpLayerSchema] = useState<TableSchema | null>(null);
  const [bmpSchemaLoading, setBmpSchemaLoading] = useState(false);
  const [sampleRecord, setSampleRecord] = useState<Record<string, any> | null>(null);
  const [dbSampleLoading, setDbSampleLoading] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState<Record<string, any>[] | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [importSqlText, setImportSqlText] = useState("");
  const [importError, setImportError] = useState<string | null>(null);
  const [showImportSection, setShowImportSection] = useState(false);

  const schemaTableKeys = useMemo(
    () => Object.keys(schema).filter((t) => !t.startsWith("ckan_data_generic")),
    []
  );
  const bmpLayersList = useMemo(() => getBmpLayersList(), []);

  const baseSchema: TableSchema | undefined = useMemo(() => {
    if (!baseTable) return undefined;
    if (isBmpBaseTable(baseTable)) return bmpLayerSchema ?? undefined;
    return schema[baseTable];
  }, [baseTable, bmpLayerSchema]);

  // Cargar schema + ejemplo de capa BMP cuando se selecciona una
  useEffect(() => {
    if (!baseTable || !isBmpBaseTable(baseTable)) {
      setBmpLayerSchema(null);
      setSampleRecord(null);
      return;
    }
    const layerId = getBmpLayerIdFromBaseTable(baseTable);
    const url = layerId ? getBmpLayerUrl(layerId) : null;
    if (!url) {
      setBmpLayerSchema(null);
      setSampleRecord(null);
      return;
    }
    setBmpSchemaLoading(true);
    fetch(url)
      .then((r) => r.json())
      .then((geojson) => {
        const features = geojson?.features;
        const fields =
          features?.length && features[0].properties
            ? Object.keys(features[0].properties)
            : [];
        const municipioRelationField = fields.includes("CODMUN") ? "CODMUN" : "c_mun";
        setBmpLayerSchema({
          name: baseTable,
          fields,
          relations: [
            { table: "municipios", localField: municipioRelationField, foreignField: "codigo_ine", type: "many-to-one" },
          ],
        });
        setSampleRecord(features?.length && features[0].properties ? { ...features[0].properties } : null);
      })
      .catch(() => {
        setBmpLayerSchema(null);
        setSampleRecord(null);
      })
      .finally(() => setBmpSchemaLoading(false));
  }, [baseTable]);

  // Cargar 1 registro de ejemplo para tablas BD
  useEffect(() => {
    if (!baseTable || isBmpBaseTable(baseTable)) {
      if (!isBmpBaseTable(baseTable)) setSampleRecord(null);
      return;
    }
    if (schemaTableKeys.indexOf(baseTable) === -1) return;
    setDbSampleLoading(true);
    Promise.resolve(
      supabase.from(baseTable).select("*").limit(1).maybeSingle()
    )
      .then(({ data }) => {
        setSampleRecord(data && typeof data === "object" ? (data as Record<string, any>) : null);
      })
      .catch(() => setSampleRecord(null))
      .finally(() => setDbSampleLoading(false));
  }, [baseTable, schemaTableKeys]);

  // Efecto para inicializar desde la configuración existente
  useEffect(() => {
    if (initialConfig) {
      setBaseTable(initialConfig.baseTable || "");
      const selected = initialConfig.selectedFields || [];
      const groupBy = initialConfig.groupByFields || [];
      const merged = [...new Set([...selected, ...groupBy])];
      const selClean = removeConcatCommaArtifacts(merged);
      const grpClean = removeConcatCommaArtifacts(groupBy);
      setSelectedFields(selClean);
      setJoins(initialConfig.joins || []);
      setFilters(initialConfig.filters || []);
      setGroupByFields(grpClean);
      setOrderByRaw(initialConfig.orderByRaw || "");
      setLimit(initialConfig.limit != null ? initialConfig.limit : DEFAULT_LIMIT);
      setOffset(initialConfig.offset || 0);
      
      // Inicializar aggregation si existe
      if (initialConfig.agreggation) {
        // Inicializar aggregateConfig con las agregaciones
        const initialAggregateConfig: any = {};
        const aliases = (initialConfig as any).fieldAliases || {};
        initialConfig.agreggation.forEach((agg: AggregationItem) => {
          initialAggregateConfig[agg.field] = {
            function: agg.agreggation,
            alias: aliases[agg.field] ?? agg.field,
            ...(agg.innerSql?.trim() ? { innerSql: agg.innerSql.trim() } : {}),
          };
        });
        if (initialConfig.selectedFields) {
          initialConfig.selectedFields.forEach((field: string) => {
            if (!initialAggregateConfig[field]) {
              initialAggregateConfig[field] = { function: "", alias: aliases[field] ?? field };
            }
          });
        }
        const comp = initialConfig.computedColumnExpressions;
        if (comp && typeof comp === "object") {
          Object.entries(comp).forEach(([fieldId, innerSql]) => {
            if (!initialAggregateConfig[fieldId]) {
              initialAggregateConfig[fieldId] = {
                function: "",
                alias: aliases[fieldId] ?? fieldId,
              };
            }
            initialAggregateConfig[fieldId] = {
              ...initialAggregateConfig[fieldId],
              innerSql: String(innerSql).trim(),
            };
          });
        }
        Object.keys(initialAggregateConfig).forEach((k) => {
          if (!selClean.includes(k)) delete initialAggregateConfig[k];
        });
        setAggregateConfig(initialAggregateConfig);
      } else if (initialConfig.selectedFields) {
        const aliases = (initialConfig as any).fieldAliases || {};
        const initialAggregateConfig: any = {};
        initialConfig.selectedFields.forEach((field: string) => {
          initialAggregateConfig[field] = { function: "", alias: aliases[field] ?? field };
        });
        const compElse = initialConfig.computedColumnExpressions;
        if (compElse && typeof compElse === "object") {
          Object.entries(compElse).forEach(([fieldId, innerSql]) => {
            if (!initialAggregateConfig[fieldId]) {
              initialAggregateConfig[fieldId] = {
                function: "",
                alias: aliases[fieldId] ?? fieldId,
              };
            }
            initialAggregateConfig[fieldId] = {
              ...initialAggregateConfig[fieldId],
              innerSql: String(innerSql).trim(),
            };
          });
        }
        Object.keys(initialAggregateConfig).forEach((k) => {
          if (!selClean.includes(k)) delete initialAggregateConfig[k];
        });
        setAggregateConfig(initialAggregateConfig);
      }
    }
  }, [initialConfig]);

  const agreggation = useMemo((): AggregationItem[] => {
    const out: AggregationItem[] = [];
    Object.entries(aggregateConfig).forEach(([field, config]) => {
      if (config.function?.trim()) {
        const item: AggregationItem = {
          field,
          agreggation: config.function,
        };
        if (config.innerSql?.trim()) item.innerSql = config.innerSql.trim();
        out.push(item);
      }
    });
    return out;
  }, [aggregateConfig]);

  useEffect(() => {
    if (isConfigSaved && isSqlSaved && onSaved) {
      onSaved();
    }
  }, [isConfigSaved, isSqlSaved, onSaved]);

  /** Configuración derivada del estado actual del builder (debe coincidir con el SQL generado). */
  const buildQueryConfigToSave = (): QueryBuilderConfig => {
    const fieldAliases: Record<string, string> = {};
    const computedColumnExpressions: Record<string, string> = {};
    selectedFieldsSanitized.forEach((f) => {
      const alias = aggregateConfig[f]?.alias;
      if (alias != null && alias.trim() !== "" && alias !== f)
        fieldAliases[f] = alias.trim();
      if (f.startsWith("__computed_expr_")) {
        const inner = aggregateConfig[f]?.innerSql?.trim();
        if (inner) computedColumnExpressions[f] = inner;
      }
    });
    return {
      baseTable,
      selectedFields: selectedFieldsSanitized,
      joins,
      filters,
      groupByFields: groupByFieldsSanitized,
      sql,
      orderByRaw: orderByRaw || undefined,
      limit,
      offset,
      agreggation,
      ...(Object.keys(fieldAliases).length > 0 ? { fieldAliases } : {}),
      ...(Object.keys(computedColumnExpressions).length > 0
        ? { computedColumnExpressions }
        : {}),
    };
  };

  const handleSaveConfig = () => {
    if (!baseTable) {
      alert("Por favor, selecciona una tabla base primero");
      return;
    }

    const configToSave = buildQueryConfigToSave();

    if (onSaveConfig) {
      onSaveConfig(configToSave, sql);
      setIsConfigSaved(true);

      if (isSqlSaved && onSaved) {
        onSaved();
      }
    }
  };

  const runPreview = async () => {
    if (!baseTable) return;
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewData(null);
    setShowPreviewModal(true);
    try {
      const snap = buildQueryConfigToSave();
      const previewConfig: any = {
        dataSource: "custom_query",
        componentType: "table",
        customQuery: {
          enabled: true,
          sql: sql || undefined,
          config: {
            baseTable,
            selectedFields,
            joins,
            filters,
            groupByFields,
            agreggation,
            orderByRaw: orderByRaw || undefined,
            fieldAliases: snap.fieldAliases,
            computedColumnExpressions: snap.computedColumnExpressions,
            limit: 4,
            offset: 0,
          },
        },
      };
      const result = await executeComponentQuery(previewConfig);
      const rows = Array.isArray(result) ? result : result?.data ?? [];
      setPreviewData(rows.slice(0, 4));
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : "Error al ejecutar la consulta");
      setPreviewData([]);
    } finally {
      setPreviewLoading(false);
    }
  };

  const suggestComponentTypes = (rows: Record<string, any>[]): string[] => {
    if (!rows.length) return ["table"];
    const keys = Object.keys(rows[0]);
    const numericKeys = keys.filter((k) => {
      const v = rows[0][k];
      return typeof v === "number" || (typeof v === "string" && !Number.isNaN(Number(v)) && v !== "");
    });
    const otherKeys = keys.filter((k) => !numericKeys.includes(k));
    const suggestions: string[] = ["table"];
    if (numericKeys.length >= 1 && otherKeys.length >= 1) {
      suggestions.push("bar-chart", "stacked-bar", "pie-chart");
    }
    if (numericKeys.length >= 2) suggestions.push("scatter-plot");
    if (keys.some((k) => /fecha|date|year|año/i.test(k))) suggestions.push("line-chart", "area-chart");
    return [...new Set(suggestions)];
  };

  const handleImportSql = () => {
    setImportError(null);
    const sql = importSqlText.trim();
    if (!sql) {
      setImportError("Pega una consulta SELECT.");
      return;
    }
    const parsed = parseSelectSql(sql);
    if (!parsed.ok) {
      setImportError(parsed.message);
      return;
    }
    const c = parsed.config;
    setBaseTable(c.baseTable);
    setSelectedFields(c.selectedFields);
    setJoins(c.joins);
    setFilters(
      c.filters.map((f) => ({
        field: f.field,
        operator: f.operator as any,
        value: f.value,
      }))
    );
    setGroupByFields(c.groupByFields);
    setOrderByRaw(c.orderByRaw || "");
    setLimit(c.limit);
    setOffset(c.offset);
    const newAgg: typeof aggregateConfig = {};
    // Campos no agregados (alias si existe)
    c.selectedFields.forEach((f) => {
      newAgg[f] = { function: "", alias: (c.aggregateAliases && c.aggregateAliases[f]) ?? f };
    });
    // Campos agregados: función + alias (+ expresión interna si aplica)
    c.aggregation.forEach((a) => {
      newAgg[a.field] = {
        function: a.agreggation,
        alias: (c.aggregateAliases && c.aggregateAliases[a.field]) ?? a.field,
        ...(a.innerSql?.trim() ? { innerSql: a.innerSql.trim() } : {}),
      };
    });
    setAggregateConfig(newAgg);
    setIsConfigSaved(false);
    setIsSqlSaved(false);
    setImportError(null);
    setImportSqlText("");
    setShowImportSection(false);
    setSampleRecord(null);
    setShowPreviewModal(true);
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewData(null);
    const previewConfig: any = {
      dataSource: "custom_query",
      componentType: "table",
        customQuery: {
        enabled: true,
        sql,
        config: {
          baseTable: c.baseTable,
          selectedFields: c.selectedFields,
          joins: c.joins,
          filters: c.filters,
          groupByFields: c.groupByFields,
          agreggation: c.aggregation,
          orderByRaw: c.orderByRaw || undefined,
          fieldAliases: c.aggregateAliases && Object.keys(c.aggregateAliases).length ? c.aggregateAliases : undefined,
          limit: 4,
          offset: 0,
        },
      },
    };
    executeComponentQuery(previewConfig)
      .then((result) => {
        const rows = Array.isArray(result) ? result : result?.data ?? [];
        setPreviewData(rows.slice(0, 4));
      })
      .catch((err) => {
        setPreviewError(err instanceof Error ? err.message : "Error al ejecutar la consulta");
        setPreviewData([]);
      })
      .finally(() => setPreviewLoading(false));
  };

  const handleSaveSql = () => {
    const currentSql = sql;
    if (!currentSql && !isBmpBaseTable(baseTable)) {
      alert("No hay SQL para guardar");
      return;
    }
    if (onSaveSql) {
      onSaveSql(currentSql || "");
    }
    // El SQL mostrado se genera desde el estado del builder; si solo se actualizaba `sql`
    // en el padre, `customQuery.config` quedaba obsoleto (p. ej. columnas de tabla / gráficos).
    if (baseTable && onSaveConfig) {
      onSaveConfig(buildQueryConfigToSave(), currentSql || "");
    }
    setIsSqlSaved(true);
    if (isConfigSaved && onSaved) onSaved();
  };

  // Obtener joins disponibles para la tabla actual
  const getJoinsForTable = (tableName: string, schemaOverride?: TableSchema): string[] => {
    const tableSchema = schemaOverride ?? schema[tableName];
    if (!tableSchema?.relations) return [];
    return tableSchema.relations
      .map((relation) => relation.table)
      .filter(
        (table) => !joins.some((j) => j.table === table) && table !== baseTable
      );
  };

  const availableFields = useMemo(() => {
    if (!baseSchema) return [];

    const fields: string[] = [];

    if (isBmpBaseTable(baseTable)) {
      fields.push(...baseSchema.fields);
      joins.forEach((join) => {
        const joinSchema = schema[join.table];
        if (joinSchema) {
          fields.push(...joinSchema.fields.map((f) => `${joinSchema.name}.${f}`));
        }
      });
      return fields;
    }
    if (baseTable.startsWith("ckan_data_generic[")) {
      fields.push(...baseSchema.fields);
      return fields;
    }

    fields.push(...baseSchema.fields.map((f) => `${baseSchema.name}.${f}`));
    joins.forEach((join) => {
      const joinSchema = schema[join.table];
      if (joinSchema) {
        fields.push(...joinSchema.fields.map((f) => `${joinSchema.name}.${f}`));
      }
    });
    return fields;
  }, [baseSchema, joins, baseTable]);

  // Campos disponibles para GROUP BY (todos los campos menos los que tienen funciones de agregación)
  const groupByAvailableFields = useMemo(() => {
    const base = availableFields.filter((field) => !aggregateConfig[field]?.function);
    const computed = selectedFields.filter(
      (f) => f.startsWith("__computed_expr_") && !aggregateConfig[f]?.function
    );
    const seen = new Set<string>();
    const out: string[] = [];
    for (const f of [...base, ...computed]) {
      if (!seen.has(f)) {
        seen.add(f);
        out.push(f);
      }
    }
    return out;
  }, [availableFields, aggregateConfig, selectedFields]);

  const addJoin = (table: string, fromTable?: string) => {
    const sourceTable = fromTable || baseSchema?.name;
    if (!sourceTable || !baseSchema) return;

    const sourceSchema = sourceTable === baseTable ? baseSchema : schema[sourceTable];
    if (!sourceSchema?.relations) return;

    const relation = sourceSchema.relations.find((r) => r.table === table);
    if (!relation) return;

    setJoins((prev) => [
      ...prev,
      {
        table,
        on: {
          localField: `${sourceSchema.name}.${relation.localField}`,
          foreignField: `${table}.${relation.foreignField}`,
        },
      },
    ]);
  };

  const removeJoin = (index: number) => {
    const joinTable = joins[index].table;
    setJoins(prev => prev.filter((_, i) => i !== index));
    
    // Remover campos de la tabla eliminada
    setSelectedFields(prev => 
      prev.filter(field => !field.startsWith(`${joinTable}.`))
    );
    setGroupByFields(prev =>
      prev.filter(field => !field.startsWith(`${joinTable}.`))
    );
    
    // Limpiar configuración de agregación para campos de la tabla eliminada
    const newAggregateConfig = { ...aggregateConfig };
    Object.keys(newAggregateConfig).forEach(field => {
      if (field.startsWith(`${joinTable}.`)) {
        delete newAggregateConfig[field];
      }
    });
    setAggregateConfig(newAggregateConfig);
  };

  const addFilter = () => {
    if (!availableFields.length) return;
    setFilters((prev) => [
      ...prev,
      {
        field: availableFields[0],
        operator: "=",
        value: "",
      },
    ]);
  };

  const removeFilter = (index: number) => {
    setFilters(prev => prev.filter((_, i) => i !== index));
  };

  const handleAggregateFunctionChange = (field: string, func: string) => {
    if (field.startsWith("__computed_expr_")) {
      return;
    }
    setAggregateConfig(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        function: func,
        alias: prev[field]?.alias || field,
      },
    }));

    // Si se selecciona una función de agregación, remover de GROUP BY
    if (func && groupByFields.includes(field)) {
      setGroupByFields(prev => prev.filter(f => f !== field));
    }

    // Si se quita la función de agregación, permitir que esté en GROUP BY
    if (!func && !selectedFields.includes(field)) {
      setSelectedFields(prev => [...prev, field]);
    }
  };

  const handleAggregateAliasChange = (field: string, alias: string) => {
    setAggregateConfig(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        alias,
      },
    }));
  };

  const expressionAggregateFieldIds = useMemo(
    () => selectedFields.filter((f) => f.startsWith("__agg_expr_")),
    [selectedFields]
  );

  const computedExpressionFieldIds = useMemo(
    () => selectedFields.filter((f) => f.startsWith("__computed_expr_")),
    [selectedFields]
  );

  const addExpressionAggregate = () => {
    const id = `__agg_expr_${Date.now()}`;
    setSelectedFields((prev) => [...prev, id]);
    setAggregateConfig((prev) => ({
      ...prev,
      [id]: {
        function: "SUM",
        alias: "total",
        innerSql: "",
      },
    }));
  };

  const removeExpressionAggregateField = (field: string) => {
    setSelectedFields((prev) => prev.filter((x) => x !== field));
    setGroupByFields((prev) => prev.filter((x) => x !== field));
    setAggregateConfig((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const addComputedExpressionColumn = () => {
    const id = `__computed_expr_${Date.now()}`;
    setSelectedFields((prev) => [...prev, id]);
    setAggregateConfig((prev) => ({
      ...prev,
      [id]: {
        function: "",
        alias: "municipio_formacion",
        innerSql: "",
      },
    }));
  };

  const removeComputedExpressionField = (field: string) => {
    removeExpressionAggregateField(field);
  };

  const sqlEscapeAlias = (alias: string) => alias.replace(/"/g, '""');

  const handleInnerSqlChange = (field: string, innerSql: string) => {
    setAggregateConfig((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        function: field.startsWith("__computed_expr_")
          ? ""
          : prev[field]?.function || "SUM",
        alias: prev[field]?.alias || field,
        innerSql,
      },
    }));
  };

  /** Quita fragmentos rotos de CONCAT(a,b,c) al partir por comas (configs antiguas). */
  const selectedFieldsSanitized = useMemo(
    () => removeConcatCommaArtifacts(selectedFields),
    [selectedFields]
  );
  const groupByFieldsSanitized = useMemo(
    () => removeConcatCommaArtifacts(groupByFields),
    [groupByFields]
  );

  const sql = useMemo(() => {
    if (isBmpBaseTable(baseTable)) {
      return ""; // Las capas BMP no generan SQL; los datos se cargan desde GeoJSON
    }

    if (!baseSchema) return "";

    // Construir la lista de campos seleccionados con funciones de agregación
    const selectFields = selectedFieldsSanitized.map((field) => {
      const aggConfig = aggregateConfig[field];

      if (field.startsWith("__computed_expr_")) {
        const alias = sqlEscapeAlias((aggConfig?.alias || field).trim());
        const inner = aggConfig?.innerSql?.trim();
        if (!inner) {
          return `NULL::text AS "${alias}"`;
        }
        return `(${inner}) AS "${alias}"`;
      }

      if (aggConfig?.function && aggConfig.function.trim() !== "") {
        // Campo con función de agregación (columna simple o expresión SQL en innerSql)
        let fieldExpression: string;
        if (aggConfig.innerSql?.trim()) {
          fieldExpression = `(${aggConfig.innerSql.trim()})`;
        } else if (field.startsWith("__agg_expr_")) {
          fieldExpression = "NULL";
        } else {
          fieldExpression = field;
        }
        
        // Para funciones AVG y SUM, agregar ::numeric si el campo es de CKAN
        if (baseTable.startsWith('ckan_data_generic[')) {
          if (aggConfig.function === "AVG" || aggConfig.function === "SUM") {
            fieldExpression = `(${fieldExpression})::numeric`;
          }
        }
        
        return `${aggConfig.function}(${fieldExpression}) AS "${sqlEscapeAlias(
          (aggConfig.alias || field).trim()
        )}"`;
      } else {
        // Campo normal - usar el alias si está configurado, si no usar el campo
        const alias = sqlEscapeAlias((aggregateConfig[field]?.alias || field).trim());
        return `${field} AS "${alias}"`;
      }
    });

    const select = selectFields.length > 0
      ? selectFields.join(", ")
      : "*";

    let query = `SELECT ${select} FROM ${baseSchema.name}`;

    // Joins
    joins.forEach((j) => {
      query += ` LEFT JOIN ${j.table} ON ${j.on.localField} = ${j.on.foreignField}`;
    });

    // WHERE clause
    if (filters.length > 0) {
      const where = filters
        .filter((f) => f.value !== "")
        .map(
          (f) =>
            `${f.field} ${f.operator} '${
              f.operator === "ILIKE"
                ? `%${f.value}%`
                : f.value
            }'`
        )
        .join(" AND ");

      if (where) query += ` WHERE ${where}`;  
    }

    // GROUP BY clause (expresiones calculadas repiten la misma expresión que en SELECT)
    if (groupByFieldsSanitized.length > 0) {
      const groupParts = groupByFieldsSanitized.map((gf) => {
        if (gf.startsWith("__computed_expr_")) {
          const inner = aggregateConfig[gf]?.innerSql?.trim();
          return inner ? `(${inner})` : "NULL";
        }
        return gf;
      });
      query += ` GROUP BY ${groupParts.join(", ")}`;
    }

    // ORDER BY clause (texto crudo importado o definido por el usuario)
    if (orderByRaw && orderByRaw.trim() !== "") {
      query += ` ORDER BY ${orderByRaw.trim()}`;
    }

    query += ` LIMIT ${limit} OFFSET ${offset}`;

    return query;
  }, [
    baseSchema,
    selectedFieldsSanitized,
    joins,
    filters,
    groupByFieldsSanitized,
    aggregateConfig,
    orderByRaw,
    limit,
    offset,
    baseTable,
  ]);

  const renderSaveButtons = () => (
    <div className="flex flex-wrap gap-2 mt-4 p-4 border-t">
      <button
        className={`px-4 py-2 rounded ${
          !baseTable 
            ? "bg-gray-300 cursor-not-allowed" 
            : "bg-blue-500 hover:bg-blue-600 text-white"
        }`}
        onClick={handleSaveConfig}
        disabled={!baseTable}
      >
        {isConfigSaved ? "✓ Configuración Guardada" : "Guardar Configuración"}
      </button>
      
      <button
        className={`px-4 py-2 rounded ${
          !sql && !isBmpBaseTable(baseTable)
            ? "bg-gray-300 cursor-not-allowed"
            : "bg-green-500 hover:bg-green-600 text-white"
        }`}
        onClick={handleSaveSql}
        disabled={!sql && !isBmpBaseTable(baseTable)}
      >
        {isSqlSaved ? "✓ SQL Guardado" : isBmpBaseTable(baseTable) ? "Confirmar (capa BMP)" : "Guardar SQL"}
      </button>

      <button
        className={`px-4 py-2 rounded ${
          !baseTable ? "bg-gray-300 cursor-not-allowed" : "bg-violet-500 hover:bg-violet-600 text-white"
        }`}
        onClick={runPreview}
        disabled={!baseTable}
        title="Ejecutar la consulta y ver hasta 4 registros de muestra"
      >
        Ejecutar y ver muestra
      </button>

      {(isConfigSaved || isSqlSaved) && (
        <button
          className="px-4 py-2 rounded bg-yellow-500 hover:bg-yellow-600 text-white"
          onClick={() => {
            if (onSaved) {
              onSaved();
            }
          }}
        >
          Continuar sin Ambos Guardados
        </button>
      )}
    </div>
  );

  return (
    <>
    <div className="p-4 space-y-4 text-sm">
      <h2 className="font-bold text-lg">Query Builder</h2>

      {/* Importar query desde texto */}
      <div className="border rounded-lg overflow-hidden">
        <button
          type="button"
          className="w-full flex items-center justify-between px-4 py-2 bg-gray-100 hover:bg-gray-200 text-left font-medium"
          onClick={() => setShowImportSection((v) => !v)}
        >
          <span>Importar query desde texto</span>
          <span className="text-gray-500">{showImportSection ? "▼" : "▶"}</span>
        </button>
        {showImportSection && (
          <div className="p-4 border-t space-y-3">
            <p className="text-xs text-gray-600">
              Pega una consulta <strong>SELECT</strong>. Se cargará la configuración del builder y podrás ver una muestra de resultados y qué tipo de componente conviene para los datos.
            </p>
            <textarea
              className="w-full h-32 border rounded p-2 font-mono text-xs"
              placeholder="SELECT ... FROM tabla LEFT JOIN ... ON ... WHERE ... GROUP BY ... LIMIT ..."
              value={importSqlText}
              onChange={(e) => {
                setImportSqlText(e.target.value);
                setImportError(null);
              }}
            />
            {importError && (
              <p className="text-red-600 text-sm">{importError}</p>
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="px-4 py-2 rounded bg-emerald-600 text-white hover:bg-emerald-700"
                onClick={handleImportSql}
              >
                Importar y ver muestra
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50"
                onClick={() => {
                  setImportSqlText("");
                  setImportError(null);
                }}
              >
                Limpiar
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Base table */}
      <div>
        <label className="font-semibold">Tabla base</label>
        <select
          className="border ml-2 px-2 py-1 rounded"
          value={baseTable}
          onChange={(e) => {
            setBaseTable(e.target.value);
            setSampleRecord(null);
            setSelectedFields([]);
            setJoins([]);
            setFilters([]);
            setGroupByFields([]);
            setOrderByRaw("");
            setAggregateConfig({});
            setIsConfigSaved(false);
            setIsSqlSaved(false);
          }}
          disabled={bmpSchemaLoading}
        >
          <option value="">-- seleccionar --</option>
          <optgroup label="Tablas BD">
            {schemaTableKeys.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </optgroup>
          <optgroup label="Capas BMP (GeoJSON)">
            {bmpLayersList.map((layer) => (
              <option key={layer.id} value={bmpBaseTableValue(layer.id)}>
                {layer.displayName}
              </option>
            ))}
          </optgroup>
        </select>
        {(bmpSchemaLoading || dbSampleLoading) && (
          <span className="ml-2 text-xs text-gray-500">
            {bmpSchemaLoading ? "Cargando atributos…" : "Cargando ejemplo…"}
          </span>
        )}
      </div>

      {/* Vista previa: campos + 1 registro de ejemplo */}
      {baseTable && baseSchema && (
        <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
          <h3 className="font-semibold text-sm text-gray-700">
            {isBmpBaseTable(baseTable)
              ? `Capa: ${getBmpLayerById(getBmpLayerIdFromBaseTable(baseTable) ?? "")?.displayName ?? baseTable}`
              : `Tabla: ${baseTable}`}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Campos ({baseSchema.fields.length})</p>
              <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                {baseSchema.fields.map((f) => (
                  <span key={f} className="px-2 py-0.5 bg-white border rounded text-xs font-mono">
                    {f}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Ejemplo (1 registro)</p>
              {sampleRecord ? (
                <div className="bg-white border rounded p-2 overflow-x-auto max-h-24 overflow-y-auto">
                  <table className="min-w-full text-xs">
                    <tbody>
                      {baseSchema.fields.slice(0, 8).map((f) => (
                        <tr key={f}>
                          <td className="font-mono text-gray-500 pr-2 align-top">{f}</td>
                          <td className="break-all">
                            {sampleRecord[f] != null ? String(sampleRecord[f]) : "—"}
                          </td>
                        </tr>
                      ))}
                      {baseSchema.fields.length > 8 && (
                        <tr><td colSpan={2} className="text-gray-400">… y {baseSchema.fields.length - 8} más</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-xs text-gray-400">Sin datos de ejemplo</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Joins (capas BMP pueden unir con municipios por CODMUN) */}
      {baseSchema && (
        <div>
          <label className="font-semibold">Joins</label>
          <div className="space-y-2 mt-2">
            {joins.map((join, index) => (
              <div key={index} className="flex items-center gap-2 border p-2 rounded">
                <div className="flex-1">
                  <span className="font-medium">{join.table}</span>
                  <div className="text-xs text-gray-600">
                    ON {join.on.localField} = {join.on.foreignField}
                  </div>
                </div>
                <button
                  className="text-red-500 px-2 hover:bg-red-50 rounded"
                  onClick={() => removeJoin(index)}
                >
                  ✕
                </button>
              </div>
            ))}
            
            <div className="flex flex-wrap gap-2">
              {getJoinsForTable(baseSchema.name, baseSchema).map((table) => (
                <button
                  key={`base-${table}`}
                  className="border px-2 py-1 bg-blue-50 hover:bg-blue-100 rounded text-xs"
                  onClick={() => addJoin(table)}
                >
                  + {table}
                </button>
              ))}
              
              {joins.flatMap((join) =>
                getJoinsForTable(join.table).map((table) => (
                  <button
                    key={`${join.table}-${table}`}
                    className="border px-2 py-1 bg-green-50 hover:bg-green-100 rounded text-xs"
                    onClick={() => addJoin(table, join.table)}
                  >
                    + {table} (desde {join.table})
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Fields with Aggregate Functions */}
      {availableFields.length > 0 && (
        <div>
          <label className="font-semibold">Campos</label>
          <div className="mt-1 border rounded overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Seleccionar</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Campo</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Función de Agregación</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Alias</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {availableFields.map((f) => {
                  const isAggregated = !!aggregateConfig[f]?.function && aggregateConfig[f]?.function.trim() !== "";
                  const currentAggregateFunction = aggregateConfig[f]?.function;
                  
                  return (
                    <tr key={f} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedFields.includes(f) || isAggregated}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedFields((prev) => [...prev, f]);
                              // Inicializar alias con el campo completo si no existe
                              if (!aggregateConfig[f]) {
                                setAggregateConfig(prev => ({
                                  ...prev,
                                  [f]: {
                                    function: "",
                                    alias: f
                                  }
                                }));
                              }
                            } else {
                              setSelectedFields((prev) => prev.filter((x) => x !== f));
                              // Si se deselecciona, quitar también de GROUP BY
                              setGroupByFields(prev => prev.filter(gf => gf !== f));
                              // Y quitar configuración de agregación si existe
                              if (aggregateConfig[f]) {
                                const newConfig = { ...aggregateConfig };
                                delete newConfig[f];
                                setAggregateConfig(newConfig);
                              }
                            }
                          }}
                          disabled={isAggregated}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-xs truncate" title={f}>{f}</span>
                      </td>
                      <td className="px-3 py-2">
                        <div>
                          <select
                            className="w-full border rounded px-2 py-1 text-xs"
                            value={aggregateConfig[f]?.function || ""}
                            onChange={(e) => handleAggregateFunctionChange(f, e.target.value)}
                            disabled={!selectedFields.includes(f) && !isAggregated}
                          >
                            {AGGREGATE_FUNCTIONS.map((func) => (
                              <option key={func || "none"} value={func}>
                                {func || "Ninguna"}
                              </option>
                            ))}
                          </select>
                          {currentAggregateFunction && currentAggregateFunction.trim() !== "" && AGGREGATE_DESCRIPTIONS[currentAggregateFunction as AggregateFunctionType] && (
                              <div className="mt-1 p-2 bg-blue-50 border border-blue-100 rounded text-xs text-blue-800">
                                <strong>{currentAggregateFunction}:</strong> {AGGREGATE_DESCRIPTIONS[currentAggregateFunction as AggregateFunctionType]}
                                {baseTable.startsWith('ckan_data_generic[') && 
                                (currentAggregateFunction === "AVG" || currentAggregateFunction === "SUM") && (
                                  <div className="mt-1 text-blue-600">
                                    <em>Nota: Se agregará ::numeric al campo automáticamente</em>
                                  </div>
                                )}
                              </div>
                            )}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          className="w-full border rounded px-2 py-1 text-xs"
                          value={aggregateConfig[f]?.alias || f}
                          onChange={(e) => handleAggregateAliasChange(f, e.target.value)}
                          placeholder="Alias para el campo"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {baseSchema && (
        <div className="mt-4">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <label className="font-semibold">Agregados por expresión SQL</label>
            <button
              type="button"
              className="border px-2 py-1 bg-amber-50 hover:bg-amber-100 rounded text-xs"
              onClick={addExpressionAggregate}
            >
              + Agregar (SUM/AVG sobre expresión)
            </button>
          </div>
          <p className="text-xs text-gray-600 mb-2">
            Para{" "}
            <code className="bg-gray-100 px-1 rounded text-[11px]">
              SUM(COALESCE(tabla.col_a,0)+COALESCE(tabla.col_b,0))
            </code>
            , escribe solo la parte interior (sin SUM). Usa nombres cualificados. En CKAN, usa la sintaxis JSONB adecuada dentro de la expresión.
          </p>
          {expressionAggregateFieldIds.length > 0 && (
            <div className="space-y-3 border rounded p-3 bg-amber-50/40">
              {expressionAggregateFieldIds.map((f) => (
                <div key={f} className="border rounded p-3 bg-white space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs font-mono text-gray-500 truncate max-w-[60%]" title={f}>
                      {f}
                    </span>
                    <button
                      type="button"
                      className="text-red-600 text-xs hover:underline"
                      onClick={() => removeExpressionAggregateField(f)}
                    >
                      Quitar
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-gray-600">Función</label>
                      <select
                        className="w-full border rounded px-2 py-1 text-xs mt-0.5"
                        value={aggregateConfig[f]?.function || "SUM"}
                        onChange={(e) => handleAggregateFunctionChange(f, e.target.value)}
                      >
                        {AGGREGATE_FUNCTIONS.filter((fn) => fn !== "").map((func) => (
                          <option key={func} value={func}>
                            {func}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">Alias (nombre de columna en resultados)</label>
                      <input
                        type="text"
                        className="w-full border rounded px-2 py-1 text-xs mt-0.5"
                        value={aggregateConfig[f]?.alias || f}
                        onChange={(e) => handleAggregateAliasChange(f, e.target.value)}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">Expresión interior (argumento del agregado)</label>
                    <textarea
                      className="w-full border rounded px-2 py-1.5 text-xs font-mono mt-0.5 min-h-[72px]"
                      value={aggregateConfig[f]?.innerSql ?? ""}
                      onChange={(e) => handleInnerSqlChange(f, e.target.value)}
                      placeholder={`COALESCE(${baseSchema.name}.campo_a, 0) + COALESCE(${baseSchema.name}.campo_b, 0)`}
                      spellCheck={false}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 mb-1 mt-6">
            <label className="font-semibold">Columnas calculadas (expresión SQL completa)</label>
            <button
              type="button"
              className="border px-2 py-1 bg-sky-50 hover:bg-sky-100 rounded text-xs"
              onClick={addComputedExpressionColumn}
            >
              + Agregar (CONCAT, COALESCE, …)
            </button>
          </div>
          <p className="text-xs text-gray-600 mb-2">
            Genera una columna a partir de una expresión PostgreSQL (sin envolver en SUM). Ejemplo:{" "}
            <code className="bg-gray-100 px-1 rounded text-[11px]">
              {`CONCAT(municipios.nombre, ' - ', valoraciones_economicas.formacion_arbolada)`}
            </code>
            . Marca la columna en GROUP BY si agregas SUM/AVG sobre otras columnas. El alias será el nombre de
            columna en tablas y gráficos.
          </p>
          {computedExpressionFieldIds.length > 0 && (
            <div className="space-y-3 border rounded p-3 bg-sky-50/40">
              {computedExpressionFieldIds.map((f) => (
                <div key={f} className="border rounded p-3 bg-white space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs font-mono text-gray-500 truncate max-w-[60%]" title={f}>
                      {f}
                    </span>
                    <button
                      type="button"
                      className="text-red-600 text-xs hover:underline"
                      onClick={() => removeComputedExpressionField(f)}
                    >
                      Quitar
                    </button>
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">Alias (nombre de columna en resultados)</label>
                    <input
                      type="text"
                      className="w-full border rounded px-2 py-1 text-xs mt-0.5"
                      value={aggregateConfig[f]?.alias || f}
                      onChange={(e) => handleAggregateAliasChange(f, e.target.value)}
                      placeholder="municipio_formacion"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">Expresión SQL</label>
                    <textarea
                      className="w-full border rounded px-2 py-1.5 text-xs font-mono mt-0.5 min-h-[80px]"
                      value={aggregateConfig[f]?.innerSql ?? ""}
                      onChange={(e) => handleInnerSqlChange(f, e.target.value)}
                      placeholder={`CONCAT(municipios.nombre, ' - ', valoraciones_economicas.formacion_arbolada)`}
                      spellCheck={false}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Group By Fields */}
      {groupByAvailableFields.length > 0 && (
        <div>
          <label className="font-semibold">GROUP BY</label>
          <div className="grid grid-cols-3 gap-2 mt-1 max-h-40 overflow-auto border p-2 rounded">
            {groupByAvailableFields.map((f) => {
              const isSelected = selectedFields.includes(f);
              const groupByLabel =
                f.startsWith("__computed_expr_") && aggregateConfig[f]?.alias
                  ? aggregateConfig[f].alias
                  : f;
              
              return (
                <label 
                  key={f} 
                  className={`flex gap-1 items-center p-1 rounded ${
                    isSelected ? 'hover:bg-blue-50' : 'hover:bg-gray-50 opacity-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={groupByFields.includes(f)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setGroupByFields((prev) => [...prev, f]);
                        // Asegurar que el campo esté seleccionado
                        if (!selectedFields.includes(f)) {
                          setSelectedFields((prev) => [...prev, f]);
                          // Inicializar alias con el campo completo
                          if (!aggregateConfig[f]) {
                            setAggregateConfig(prev => ({
                              ...prev,
                              [f]: {
                                function: "",
                                alias: f
                              }
                            }));
                          }
                        }
                      } else {
                        setGroupByFields((prev) => prev.filter((x) => x !== f));
                      }
                    }}
                    disabled={!isSelected}
                  />
                  <span className="text-xs truncate" title={f}>{groupByLabel}</span>
                </label>
              );
            })}
          </div>
          {groupByFields.length > 0 && (
            <div className="mt-2 text-xs text-gray-600">
              <p>Campos en GROUP BY: {groupByFields.join(', ')}</p>
              <p className="mt-1">
                <strong>Nota:</strong> Los campos con funciones de agregación no pueden estar en GROUP BY.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      {availableFields.length > 0 && (
        <div>
          <label className="font-semibold">Filtros</label>
          <button
            className="ml-2 border px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs"
            onClick={addFilter}
          >
            + Agregar filtro
          </button>

          <div className="space-y-2 mt-2">
            {filters.map((f, i) => (
              <div key={i} className="flex gap-2 items-center border p-2 rounded">
                <select
                  className="border px-2 py-1 rounded text-xs flex-1"
                  value={f.field}
                  onChange={(e) =>
                    setFilters((prev) =>
                      prev.map((x, idx) =>
                        idx === i
                          ? { ...x, field: e.target.value }
                          : x
                      )
                    )
                  }
                >
                  {availableFields.map((af) => (
                    <option key={af} value={af}>
                      {af}
                    </option>
                  ))}
                </select>

                <select
                  className="border px-2 py-1 rounded text-xs w-20"
                  value={f.operator}
                  onChange={(e) =>
                    setFilters((prev) =>
                      prev.map((x, idx) =>
                        idx === i
                          ? {
                              ...x,
                              operator: e.target
                                .value as Filter["operator"],
                            }
                          : x
                      )
                    )
                  }
                >
                  {OPERATORS.map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </select>

                <input
                  className="border px-2 py-1 rounded text-xs flex-1"
                  value={f.value}
                  onChange={(e) =>
                    setFilters((prev) =>
                      prev.map((x, idx) =>
                        idx === i
                          ? { ...x, value: e.target.value }
                          : x
                      )
                    )
                  }
                  placeholder="Valor"
                />
                
                <button
                  className="text-red-500 px-2 hover:bg-red-50 rounded"
                  onClick={() => removeFilter(i)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Limit / Offset */}
      <div className="flex gap-4">
        <div>
          <label className="font-semibold">Limit</label>
          <input
            type="number"
            className="border ml-1 w-20 px-2 py-1 rounded text-sm"
            value={limit}
            min="1"
            onChange={(e) => setLimit(+e.target.value)}
          />
        </div>

        <div>
          <label className="font-semibold">Offset</label>
          <input
            type="number"
            className="border ml-1 w-20 px-2 py-1 rounded text-sm"
            value={offset}
            min="0"
            onChange={(e) => setOffset(+e.target.value)}
          />
        </div>
      </div>

      {/* SQL output */}
      <div>
        <label className="font-semibold">SQL generado</label>
        <pre className="bg-gray-100 p-4 mt-1 overflow-auto text-xs font-mono border rounded">
          {sql}
        </pre>
      </div>

      {/* Botones de guardado */}
      {renderSaveButtons()}
    </div>

    {/* Modal: ejecutar query y ver muestra */}
    {showPreviewModal && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex justify-between items-center p-4 border-b">
            <h3 className="font-semibold text-lg">Muestra de resultados (máx. 4 registros)</h3>
            <button
              type="button"
              className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              onClick={() => setShowPreviewModal(false)}
            >
              ×
            </button>
          </div>
          <div className="p-4 overflow-auto flex-1">
            {previewLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-2 border-violet-500 border-t-transparent" />
              </div>
            ) : previewError ? (
              <p className="text-red-600">{previewError}</p>
            ) : previewData && previewData.length > 0 ? (
              <>
                <div className="overflow-x-auto border rounded">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        {Object.keys(previewData[0]).map((key) => (
                          <th key={key} className="px-3 py-2 text-left font-medium text-gray-700">
                            {key}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.map((row, i) => (
                        <tr key={i} className="border-t">
                          {Object.keys(previewData[0]).map((key) => (
                            <td key={key} className="px-3 py-2 text-gray-800">
                              {row[key] != null ? String(row[key]) : "—"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-3 text-sm text-gray-600">
                  <strong>Tipos de componente recomendados para estos datos:</strong>{" "}
                  {suggestComponentTypes(previewData).join(", ")}
                </p>
              </>
            ) : (
              <p className="text-gray-500">No hay registros en el resultado.</p>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default QueryBuilder;